import React, { useContext, useState, useEffect, useMemo, Fragment } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-material.css'; // Optional theme CSS
import './ag-grid-theme.css';
import AppContext from '../../AppContext';
import { Menu, Transition, Switch } from '@headlessui/react';
import { MagnifyingGlassIcon, DocumentArrowDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import Tooltip from './CustomComponents/Tooltip';
import uniq from 'lodash/uniq';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}


const Grid = ({
    data,
    columns, 
    columnDefs,
    visibleColumns,
    autoGroupColumnDef,
    columnButtons,
    customDropdownOptions,
    groupRemoveSingleChildren,
    groupRemoveLowestSingleChildren,
    components,
    onSelectionChanged,
    onGridReady,
    editFunctionOverride,
    failureMessage,
    loading,
    editable,
    pivotable,
    onModelUpdated,
    externalFilter,
    externalFilters,
    groupDropdowns,
    onMouseOverRow,
    onMouseOutGrid,
    app_usage,
    groupDefaultExpanded,
    setInitialSelection,
    rowHeight,
    buttons, 
    pinFilters,
    actions,
    links,
    specialBreakpoint,
    exportName,
    suppressRowClickSelection=false,
    enableRangeSelection=true,
    noMargin,
    checkbox,
    checkboxPinned,
    expandAll,
    formMode,
    customExportColumnList,
    refreshFunction,
    cacheTime,
    groupColumns,
    rightSideDropdowns,
    groupHideOpenParents,
    groupUseEntireRow,
    groupWholeRowComponent,
    rowNodeIdFunction,
    enableGroupEdit,
    shadeRowLevels,
    getDataPath,
    fitColumns,
    singleRowSelection,
    bulkImport,
    bulkConvertJsonFields,
    bulkGenerateIdField,
    showMenu = true,
    manualRefresh = null,
    isManualRefreshLoading = false,
    minColumnWidth = null,
    maxColumnWidth = null
  }) => {  

    const { gridDisplay } = useContext(AppContext); 

    const [failed, setFailed] = useState(!!failureMessage);
    const [failedMessage, setFailedMessage] = useState(failureMessage);
    // const [loading, setLoading] = useState(true);
    const [shownRows, setShownRows] = useState(0);
    const [totalRows, setTotalRows] = useState(0);
    const [gridApi, setGridApi] = useState();
    // const [expandedRowIds, setExpandedRowIds] = useState([]);
    const [firstSelection, setFirstSelection] = useState(true);
    // const [gridColumnApi, setGridColumnApi] = useState();
    const [calculatedColumnDefs, setCalculatedColumnDefs] = useState();
    const [updateCount, setUpdateCount] = useState(-1);
    // const [firstLoad, setFirstLoad] = useState(true);
    // const [expanded, setExpanded] = useState(groupDefaultExpanded && groupDefaultExpanded>0);
    // const [filtersPinned, setFiltersPinned] = useState(!!pinFilters);
    // const [expandedVerticalMenu, setExpandedVerticalMenu] = useState(false);
    // const [expandedVerticalFilterMenu, setExpandedVerticalFilterMenu] = useState(false);
    const [working, setWorking] = useState(false);

    // console.log("rownodeIdfunc: ", rowNodeIdFunction);
    useEffect(() => {
      if(updateCount === 0) {
        refreshFunction && refreshFunction()
      }
      // eslint-disable-next-line
    }, [updateCount])


    const getFilteredRowCountValue = (params) => {
      let filteredRowCount = 0;
      params.api.forEachNodeAfterFilter(function (node) {
          if (!node.group) {
              filteredRowCount += 1;
          }
      });
      return filteredRowCount;
    };

    const initGrid = (params) => {
      if (onGridReady) {
        onGridReady(params);
      }
      setGridApi(params.api);
      // setGridColumnApi(params.columnApi);
    };

    useEffect(() => {
      if (data && gridApi){
        // console.log(data);
        // gridApi.setRowData(data);
        setTotalRows(data?.length);
      }
      //eslint-disable-next-line
    }, [data, gridApi]);


    useEffect(() => {
        if(gridApi && setInitialSelection && firstSelection) {
          setInitialSelection(gridApi);
          setFirstSelection(false);
        }
    }, [gridApi, setInitialSelection, firstSelection]);

    const stringAgg = (params) => {
      return params.values && params.values.join(`,`);
    }

    const stringAggUnique = (params) => {
      return params.values && uniq(params.values).join(`, `);
    }

    const stringAggWithQuotes = (params) => {
      return params.values && "'" +params.values.join(`','`) + "'";
    }

    const minAndMax = (params) => {
      const values = params.values;
      const min = Math.min(...values);
      const max = Math.max(...values);
      return values && values?.length>0 && !(isNaN(max) || isNaN(min)) ? (max === min ? min : min + " - " + max) : null
    }

    const aggFuncLookup = {
      "sum": "sum",
      "min": "min", 
      "max": "max", 
      "count": "count", 
      "avg": "avg", 
      "first": "first", 
      "last": "last",
      "stringAgg": stringAgg,
      "stringAggUnique":stringAggUnique,
      "stringAggWithQuotes": stringAggWithQuotes,
      "minAndMax": minAndMax
    }
    
    useEffect(() => {
      try {
        let newColumnDefs;
        if (!columnDefs) {
          let filteredGridDisplay = gridDisplay && gridDisplay?.filter((y) => y.app_usage===app_usage)
          if(columnButtons) {
            columnButtons.forEach((x) => {
              filteredGridDisplay = filteredGridDisplay?.filter((y) => y.source_column !== x.field)
            })
          }

          if(filteredGridDisplay?.length >0) {
            newColumnDefs = filteredGridDisplay.map((x) => ({
              field: x.source_column,
              // tooltipField: x.tooltip ? x.source_column : undefined,
              tooltipValueGetter: x.tooltip && x?.formatting?.toUpperCase() !== 'JSON' ? (params)=>(params.value) : (x.tooltip && x?.formatting?.toUpperCase() === 'JSON' ? (params) => {return params?.data && !!params?.data[`${x.source_column}`] ? JSON.stringify(params?.data[`${x.source_column}`]) : undefined} : undefined),
              headerName: x.display_alias,
              hide: !x.is_displayed,
              // rowGroup: groupColumns && groupColumns.includes(x.source_column) ? true : false,
              // rowGroupIndex: groupColumns && groupColumns.includes(x.source_column) ? groupColumns.findIndex(y => y === x.source_column) : undefined,
              editable: editable === false ? false : x.is_editable,
              headerTooltip: x.description ? x.description : undefined,
              aggFunc: aggFuncLookup[x.agg_func] ? aggFuncLookup[x.agg_func] : undefined,
              valueFormatter: (params) => {
                if (typeof x.formatting === 'undefined') {
                  return undefined;
                }
                const formattingUpper = x?.formatting?.toUpperCase();
                if (formattingUpper === 'DollarsAndCents'.toUpperCase()) {
                  return DollarsAndCents(params);
                } else if (formattingUpper === 'DollarsNoCents'.toUpperCase()) {
                  return DollarsNoCents(params);
                } else if (formattingUpper === 'NumericRoundedHundredths'.toUpperCase()) {
                  return NumericRoundedHundredths(params);
                } else if (formattingUpper === 'NumericRoundedWholeNumber'.toUpperCase()) {
                  return NumericRoundedWholeNumber(params);
                } else if (formattingUpper === 'PercentHundredths'.toUpperCase()) {
                  return PercentHundredths(params);
                } else if (formattingUpper === 'PercentWholeNumber'.toUpperCase()) {
                  return PercentWholeNumber(params);
                } else if (formattingUpper === 'Date'.toUpperCase()) {
                  return DateFormat(params);
                } else if (formattingUpper === 'DateAndTimestamp'.toUpperCase()) {
                  return DateAndTimestamp(params);
                } else if (formattingUpper === 'ISODateAndTimestamp'.toUpperCase()) {
                  return ISODateAndTimestamp(params);
                } else if (formattingUpper === 'Boolean'.toUpperCase()) {
                  return BooleanFormat(params);
                // } else if (formattingUpper === 'JSON'.toUpperCase()) {
                //   return JSONFormat(params);
                } else {
                  return undefined;
                }
              },
              cellClass: x.formatting || 'default',
              cellEditorPopup: true,
              cellEditorPopupPosition: 'over',
              cellEditor: x.is_editable && x.cell_editor === 'CellEditorDropdown' && x.data_type === 'boolean' 
                        ? 'CellEditorDropdown' 
                        : (x.cell_editor === 'CellEditorDate' ? 'CellEditorDate' 
                        : (x.cell_editor === 'CellEditorText' ? 'CellEditorText' 
                        : (x.cell_editor === 'CellEditorDropdown' && customDropdownOptions && customDropdownOptions.filter((y) => y.columnId === x.source_column)?.length >0 ? 'CellEditorDropdown': 'CellEditorText'))),
              cellEditorParams: x.is_editable && x.cell_editor === 'CellEditorDropdown' && x.data_type === 'boolean' 
                        ? {'options': [{ key: 'true', text: 'Yes', value: 'true' }, { key: 'false', text: 'No', value: 'false' }]} 
                        : (x.cell_editor === 'CellEditorDropdown' && customDropdownOptions && customDropdownOptions.filter((y) => y.columnId === x.source_column)?.length >0 ? customDropdownOptions.filter((y) => y.columnId === x.source_column)[0]?.cellEditorParams : undefined),
              cellStyle: x.is_editable && x.cell_editor === 'CellEditorDropdown' ? { "overflow": 'visible' } : undefined,
              valueGetter: x.is_editable && x.cell_editor === 'CellEditorDropdown' && x.data_type === 'boolean' ? 
                          (params) => { return {value: params?.data && params?.data[`${x.source_column}`] !== undefined ? params?.data[`${x.source_column}`] : null, toString: () => params?.data && params?.data[`${x.source_column}`] !== undefined ? ((params?.data[`${x.source_column}`] === true || params?.data[`${x.source_column}`] ==='true') ? 'Yes' : 'No') : ''}} 
                        : ( x?.formatting?.toUpperCase() === 'JSON' ? (params) => { return params?.data && !!params?.data[`${x.source_column}`] ? JSON.stringify(params?.data[`${x.source_column}`]) : undefined; } 
                        : undefined)

            }));
            
          } else if (columns) {
            newColumnDefs = columns.map((x) => ({ field: x.name, headerName: x.name }));
          }
        }

        if (columnDefs) {
          newColumnDefs = columnDefs;
        }

        if (columnButtons) {
          columnButtons.forEach((x) => {
            x.aggFunc = aggFuncLookup[x.aggFunc] ? aggFuncLookup[x.aggFunc] : x.aggFunc;
            if (!x.valueFormatter && x.formatting) {
              x.valueFormatter = (params) => {
                if (typeof x.formatting === 'undefined') {
                  return undefined;
                }
                const formattingUpper = x.formatting.toUpperCase();
                if (formattingUpper === 'DollarsAndCents'.toUpperCase()) {
                  return DollarsAndCents(params);
                } else if (formattingUpper === 'DollarsNoCents'.toUpperCase()) {
                  return DollarsNoCents(params);
                } else if (formattingUpper === 'NumericRoundedHundredths'.toUpperCase()) {
                  return NumericRoundedHundredths(params);
                } else if (formattingUpper === 'NumericRoundedWholeNumber'.toUpperCase()) {
                  return NumericRoundedWholeNumber(params);
                } else if (formattingUpper === 'PercentHundredths'.toUpperCase()) {
                  return PercentHundredths(params);
                } else if (formattingUpper === 'PercentWholeNumber'.toUpperCase()) {
                  return PercentWholeNumber(params);
                } else if (formattingUpper === 'Date'.toUpperCase()) {
                  return DateFormat(params);
                } else if (formattingUpper === 'DateAndTimestamp'.toUpperCase()) {
                  return DateAndTimestamp(params);
                } else if (formattingUpper === 'ISODateAndTimestamp'.toUpperCase()) {
                  return ISODateAndTimestamp(params);
                } else if (formattingUpper === 'Boolean'.toUpperCase()) {
                  return BooleanFormat(params);
                // } else if (formattingUpper === 'JSON'.toUpperCase()) {
                //   return JSONFormat(params);
                } else {
                  return undefined;
                }
              }
            }
            if(x.index) {
              if (!newColumnDefs) {
                newColumnDefs = [];
              }
              newColumnDefs.splice(x.index, 0, x);
            } else {
              newColumnDefs?.unshift(x);
            }
          })
        }

        if(checkbox) {
          newColumnDefs?.unshift({
              field: 'checkbox',
              headerName: '',
              checkboxSelection: true,
              headerCheckboxSelection: true,
              headerCheckboxSelectionFilteredOnly: true,
              menuTabs: [],
              maxWidth: 55,
              pinned: checkboxPinned 
          });
        }

        if(newColumnDefs && newColumnDefs?.length > 0) {
          newColumnDefs.forEach((x) => {
            x.rowGroup = !!getDataPath ? undefined : (groupColumns && groupColumns.includes(x.field) ? true : (x.rowGroup || false));
            x.rowGroupIndex = !!getDataPath ? undefined : (groupColumns && groupColumns.includes(x.field) ? groupColumns.findIndex(y => y === x.field) : x.rowGroupIndex);
          })

          if(visibleColumns?.length > 0) {
            newColumnDefs.forEach((x) => {
              x.hide = !visibleColumns.includes(x.field) ;
            })
          }
        }

        setCalculatedColumnDefs(newColumnDefs);

      } catch (error) {
        console.log('grid error: ', error)
        setFailed(true);
        setFailedMessage('There was an error retrieving the column names for this page.')
      }
    // eslint-disable-next-line
    }, [columns, columnDefs, gridDisplay, app_usage, checkbox, columnButtons, customDropdownOptions, groupColumns, visibleColumns]);


    const gridUpdated = (params) => {
      // this does auto resizing
      if(fitColumns) {
        params.api.sizeColumnsToFit()
      } else {
        if (params.columnApi.getColumns()) {
          let allColumnIds = [];
          params.columnApi.getColumns().forEach((column) => {
            allColumnIds.push(column.colId);
          });
          params.columnApi.autoSizeColumns(allColumnIds);
          params.columnApi.autoSizeColumn('ag-Grid-AutoColumn');
        }
      }
  
      if (params.newData ) {
        params.api.resetRowHeights(); //this is for resetting row height based on lots of text in columns
      }

      setShownRows(getFilteredRowCountValue(params));
  
      // if we need to do something additional when the grid changes for any reason, this is where we'd pass it in
      if (onModelUpdated) {
        onModelUpdated(params);
      }
    };
  
    if (editable === false && columnDefs) {
      columnDefs = columnDefs.map((item) => {
        item.editable = false;
        return item;
      });
    }
  
    const defaultColDef = useMemo(() => { return {
      // editable,
      resizable: true,
      sortable: true,
      filter: 'agSetColumnFilter',
      // enableCellChangeFlash: true, //maybe selectively re-enable this when we start editing stuff
      menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
      cellEditor: 'CellEditorText',
      cellClass: 'default', //this is for exporting to excel
      enablePivot: pivotable === true, //these three settings are overridden on a column by column basis if need be
      enableRowGroup: !!getDataPath ? undefined : (pivotable === true), 
      enableValue: pivotable === true, 
      maxWidth: !!maxColumnWidth ? maxColumnWidth : 500, //so stuff doesn't unreadably wide
      // autoHeight:true, //for stuff that needs to wrap
      // cellStyle: {'white-space': 'normal'} //for stuff that needs to wrap
      tooltipComponent: 'Tooltip',
      suppressKeyboardEvent: (params) => {
        if (!params.editing && params.event?.key === 'Delete') {
          return true;
        } else {
          return false;
        }
      }
    };
  },[])
  
    const getContextMenuItems = (params) => {
      const cellDetail = params.api.getFocusedCell();
      if (cellDetail !== null) {
        const menuItems = [
          'copy',
          'copyWithHeaders',
          // 'chartRange'
        ];
        return menuItems;
      }
      return null;
    };

    const processCellFromClipboard = (params) => {
      var pasteValue;
      
      switch (params.column.colDef.cellEditor) {
        case "CellEditorText":
          pasteValue = params.value;
          break;
        case "CellEditorDropdownNoButton":
          pasteValue = typeof params.column.colDef.cellEditorParams === 'function' ? params.column.colDef.cellEditorParams(params.node).options.filter(y => y.text === params.value)[0]?.value : params.column.colDef.cellEditorParams?.options?.filter(y => y.text === params.value)[0]?.value ? params.column.colDef.cellEditorParams.options.filter(y => y.text === params.value)[0]?.value : params.value //params.column.colDef.cellEditorParams.options.filter(y => y.text === params.value)[0].value;
          break;
        case "CellEditorDropdown":
          pasteValue = typeof params.column.colDef.cellEditorParams === 'function' ? params.column.colDef.cellEditorParams(params.node).options.filter(y => y.text === params.value)[0]?.value : params.column.colDef.cellEditorParams?.options?.filter(y => y.text === params.value)[0]?.value ? params.column.colDef.cellEditorParams.options.filter(y => y.text === params.value)[0]?.value : params.value //params.column.colDef.cellEditorParams.options.filter(y => y.text === params.value)[0].value;
          break;
        default:
          pasteValue = params.value;
          break;
      }
      return pasteValue;
    }

    const DollarsAndCents = (params) => {
        return params.value !== null && !isNaN(Number(params.value)) ? '$ ' + Number(params.value).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : params.value; 
    };

    const DollarsNoCents = (params) => {
        return params.value !== null && !isNaN(Number(params.value)) ? '$ ' + Number(params.value).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : params.value;
    };

    const NumericRoundedHundredths = (params) => {
      return params.value !== null && !isNaN(Number(params.value)) ? params.value.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : params.value
    };

    const NumericRoundedWholeNumber = (params) => {
      return params.value !== null && !isNaN(Number(params.value)) ? params.value.toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : params.value
    };

    const PercentHundredths = (params) => {
      return params.value !== null && !isNaN(Number(params.value)) ? (100.0*Number(params.value)).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' %' : params.value;
    };

    const PercentWholeNumber = (params) => {
      return params.value !== null && !isNaN(Number(params.value)) ? (100.0*Number(params.value)).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' %' : params.value;
    };

    // const JSONFormat = (params) => {
    //   return params.value !== null ? JSON.stringify(params.value) : params.value;
    // };

    const BooleanFormat = (params) => {
      if (params.value !== undefined && params.value !== null && params.value === false) {
        return 'No';
      } else if (params.value !== undefined && params.value !== null && params.value === true) {
        return 'Yes';
      } else {
        return params.value;
      }
    };
    
    const isDate = (date) => {
      return (new Date(date) !== "Invalid Date") && !isNaN(new Date(date));
    }

    const DateFormat = (params) => {
      return params.value && isDate(params.value) && new Date(params.value).toISOString().substring(0,10);
    };

    const DateAndTimestamp = (params) => {
      return params.value && isDate(params.value) && new Date(params.value).toLocaleDateString() + ' ' + new Date(params.value).toLocaleTimeString();
    };

    const ISODateAndTimestamp = (params) => {
      const tzoffset = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds
      const localISOTime = (new Date(new Date(params.value) - tzoffset)).toISOString().slice(0, 19)?.replace('T',' ');
      return params.value && isDate(params.value) && localISOTime;//isDate(params.value) && new Date(params.value).toLocaleDateString() + ' ' + new Date(params.value).toLocaleTimeString();
    };

    const excelStyles = [{
      id: 'default',
      font: { size: 11 }
    }, {
      id: 'DollarsAndCents',
      numberFormat: { format: '$#,##0.00' },
      font: { size: 11 }
    }, {
      id: 'DollarsNoCents',
      numberFormat: { format: '$#,##' },
      font: { size: 11 }
    }, {
      id: 'NumericRoundedHundredths',
      numberFormat: { format: '#,##0.00' },
      font: { size: 11 }
    }, {
      id: 'NumericRoundedWholeNumber',
      numberFormat: { format: '#,##' },
      font: { size: 11 }
    }, {
      id: 'PercentHundredths',
      numberFormat: { format: '#,##.00%' },
      font: { size: 11 }
    }, {
      id: 'PercentWholeNumber',
      numberFormat: { format: '#,##%' },
      font: { size: 11 }
    }, {
      id: 'Boolean',
      font: { size: 11 }
    }, {
      id: 'Date',
      font: { size: 11 }
    }, {
      id: 'DateAndTimestamp',
      font: { size: 11 }
    }
  ];

  const makeRefreshAsync = async () => {
    setWorking(true);
    await refreshFunction();
    setWorking(false);
  } 
  
    return (

      <div className='w-full h-full flex flex-col flex-1 mx-auto sm:p-6' > 


      {showMenu && <div className={(loading ? "hidden " : "") + "flex h-10 sm:rounded-t-md shrink-0 items-center gap-x-4 bg-white px-4 border-x-gray-500 border-t-gray-500 shadow-sm shadow ring-1 ring-black ring-opacity-5"}> {/* //border-gray-200  shadow-sm shadow ring-1 ring-black ring-opacity-5 */} 
            <div className="flex flex-1 gap-x-4 self-stretch ">
              {/* *******************Search Bar thingy*********************************** */}
              <div className="relative flex flex-1 mb-1 hidden xms:flex">
                <span className='hidden sm:block'>
                <label htmlFor="search-field" className="sr-only">
                  Search
                </label>
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-600"
                  aria-hidden="true"
                />
                <input
                  id="search-field"
                  className="block h-full max-w-32 border-0 py-0 pl-8 pr-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                  placeholder="Search..."
                  type="search"
                  name="search"
                  onChange={(e)=>{gridApi.setQuickFilter(e?.target?.value)}}
                />
                </span>


                {buttons && buttons?.filter((y) => !!y.toggle)?.map((x) => {
                  return <Switch.Group key={x.label} as="div" className="flex items-center">
                    <Switch
                      checked={x.checked}
                      onChange={x.disabled ? ()=>'' : x.onClick}
                      disabled={x.disabled}
                      className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
                    >
                      <span className="sr-only">{x.label}</span>
                      <span aria-hidden="true" className="pointer-events-none absolute h-full w-full rounded-md bg-white" />
                      <span
                        aria-hidden="true"
                        className={classNames(
                          x.checked ? 'bg-primary-600' : 'bg-gray-200',
                          'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out'
                        )}
                      />
                      <span
                        aria-hidden="true"
                        className={classNames(
                          x.checked ? 'translate-x-5' : 'translate-x-0',
                          'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out'
                        )}
                      />
            
                    </Switch>
                    <Switch.Label as="span" className="ml-1 mr-2 text-sm">
                      <span className="font-medium text-gray-900">{x.label}</span>
                    </Switch.Label>
                    </Switch.Group>
                })}
              </div>
              <div className="flex items-center gap-x-4 lg:gap-x-6">                

              <div className="inline-flex items-center gap-x-1.5">
              {buttons && buttons?.filter((y) => !y.toggle)?.map((x) => {
                  // return <Menu.Item as={Button} fitted='vertically' primary key={x.label} onClick={x.onClick} content={x.label} disabled={x.disabled}></Menu.Item>
                  return (<button
                      key={x.label}
                      onClick={x.onClick}
                      disabled={x.disabled}
                      type="button"
                      className="relative h-8 inline-flex items-center gap-x-1 rounded-md bg-primary-600 px-2 py-2 text-xs font-semibold text-white shadow-sm 
                        hover:bg-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:bg-primary-300"
                    >
                      {x.label}
                      {/* <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" /> */}
                    </button>)
                })}
                  
                </div>

                { !!manualRefresh &&
                <Menu as="div" className="relative">
                  <Menu.Button 
                    className="-m-1.5 flex items-center p-1.5" 
                    onClick={manualRefresh} // Refresh action directly on button click
                    disabled={isManualRefreshLoading}
                    aria-label="Refresh items"
                  >
                    <ArrowPathIcon className={(isManualRefreshLoading ? "animate-spin text-gray-400 " : "text-gray-600 hover:text-gray-900 ") + "h-5 w-5"} aria-hidden="true" />
                  </Menu.Button>
                  <Transition
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <div className="absolute opacity-0"> {/* This div is for transitions */}
                    </div>
                  </Transition>
                </Menu> }

                {/* Separator */}
                {/* <div className="h-6 w-px bg-gray-200 " aria-hidden="true" /> */}
                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <Menu.Button className="-m-1.5 flex items-center p-1.5" disabled={shownRows === 0 || !exportName}>
                    <span className="sr-only">Open export menu</span>
                    <DocumentArrowDownIcon className={(shownRows === 0 || !exportName ? "text-gray-400 " : "text-gray-600 hover:text-gray-900 " ) +"h-5 w-5 "} aria-hidden="true" />
           
                   
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                    {shownRows > 0 && shownRows < 50001 && 
                      <Menu.Item 
                        className='cursor-pointer'
                        onClick={() => gridApi.exportDataAsExcel({ fileName: exportName, sheetName: "Sheet1", skipRowGroups: true, columnKeys: customExportColumnList || undefined })}
                      >
                        <span className='block px-3 py-1 text-sm leading-6 text-gray-900 hover:bg-gray-50' >
                          Export Excel
                        </span>
                      </Menu.Item>}

                      <Menu.Item 
                        className='cursor-pointer'
                        onClick={() => gridApi.exportDataAsCsv({ fileName: exportName, skipRowGroups: true, columnKeys: customExportColumnList || undefined })}
                      >
                        <span className='block px-3 py-1 text-sm leading-6 text-gray-900 hover:bg-gray-50' >
                          Export CSV
                        </span>
                      </Menu.Item>                
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>

              <span className='flex gap-x-1'>
              <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                {Number(shownRows).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
              of
              <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                {Number(totalRows).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
              </span>
        </div>}


        <div className={(showMenu ? "sm:rounded-b-lg " : "no-menu sm:rounded-lg  ") + " ag-theme-material flex flex-col flex-1 h-64 w-full  border-x-gray-500 border-b-gray-500 shadow ring-1 ring-black ring-opacity-5"} onMouseLeave={onMouseOutGrid}>
          <AgGridReact
            processCellFromClipboard={processCellFromClipboard}
            // reactNext
            columnDefs={calculatedColumnDefs}
            rowHeight={rowHeight}
            autoGroupColumnDef={autoGroupColumnDef}
            groupRemoveSingleChildren={groupRemoveSingleChildren}
            groupRemoveLowestSingleChildren={groupRemoveLowestSingleChildren}
            groupSelectsChildren={!!getDataPath && !singleRowSelection}
            groupSelectsFiltered={true}
            onSelectionChanged={onSelectionChanged}
            onCellMouseOver={onMouseOverRow}
            enableFillHandle={true}
            undoRedoCellEditing={true}
            undoRedoCellEditingLimit={20}
            tooltipShowDelay={0}
            enableBrowserTooltips={true}
            components={{Tooltip, groupWholeRowComponent, ...components}}
            excelStyles={excelStyles}
            defaultExcelExportParams={{
              addImageToCell: function (rowIndex, col, value) {
                if (col.colDef?.cellClass !== 'image' || !value) {
                  return;
                }
                return {
                  image: {
                    id: value,
                    base64: value,
                    imageType: 'png',
                    // fitCell: false
                    // width: 40,
                    // height: 20,
                    // position: {
                    //   offsetX: 30,
                    //   offsetY: 5.5,
                    // },
                  },
                };
              },
            }}
            // components={components}
            pagination={false}
            rowData={data}
            onGridReady={initGrid}
            defaultColDef={defaultColDef}
            groupDefaultExpanded={groupDefaultExpanded || 0}
            // enableCellChangeFlash
            rowSelection={singleRowSelection ? 'single' : "multiple"}
            paginationAutoPageSize
            enableRangeSelection={enableRangeSelection}
            enableGroupEdit={enableGroupEdit}
            suppressCopyRowsToClipboard
            suppressAggFuncInHeader
            suppressRowClickSelection={suppressRowClickSelection}
            // sideBar={pivotable === true ? {
            // toolPanels: [{
            //     id: 'columns',
            //     labelDefault: 'Pivot',
            //     labelKey: 'columns',
            //     iconKey: 'columns',
            //     toolPanel: 'agColumnsToolPanel',
            // }],
            // } : false}
            getContextMenuItems={getContextMenuItems}
            onModelUpdated={gridUpdated}
            isExternalFilterPresent={externalFilter ? () => true : false}
            doesExternalFilterPass={(node) => externalFilter(node)}
            // enableCharts={true}
            rowClassRules={{'highlighted-grid-row': 'data?.highlighted === true', 'shadedGroupRow': shadeRowLevels ? '1 === 1' : ' 1 === 2'}}
            groupHideOpenParents={groupHideOpenParents}
            groupUseEntireRow={groupUseEntireRow}
            groupRowRendererParams={!!groupUseEntireRow && {innerRenderer: 'groupWholeRowComponent', suppressCount: true}}
            getRowId={rowNodeIdFunction}
            suppressAggAtRootLevel={true}
            animateRows={true}
            getDataPath={getDataPath}
            treeData={!!getDataPath}
          />
        </div>
        
      </ div>
    );
  };
  
  export default Grid;